import { ORDER_URGENCY_LABELS, type OrderSuggestion } from "@station/contracts";
import { Decimal } from "@station/core";
import { Alert, Button, Card, EmptyState, QuantityText, SkeletonRows, StatusBadge } from "@station/ui";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ClipboardList, PackagePlus } from "lucide-react";
import { type ReactElement, useId, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Field } from "../../../features/expenses/shared.js";
import {
  buildOrderPrefill,
  ORDER_URGENCY_TONE,
  remainingTime,
  runsOutSoon,
} from "../../../features/fuel/forecast-values.js";
import { useFuelPermissions, useOrderSuggestions } from "../../../features/fuel/queries.js";
import {
  FuelPage,
  fuelSearchShape,
  NoStations,
  StationField,
  Th,
  useFuelStation,
} from "../../../features/fuel/shared.js";
import { errorMessage } from "../../../lib/errors.js";
import { formatCount, formatDateTime } from "../../../lib/format.js";

const searchSchema = z.object(fuelSearchShape);

export const Route = createFileRoute("/_app/akaryakit/siparis-onerileri")({
  validateSearch: (search) => searchSchema.parse(search),
  component: OrderSuggestionsPage,
});

/**
 * Sipariş önerisi ekranı. Öneri ÜRÜN bazındadır (aynı ürünün tankları tek havuzdur) ve tanker geldiğinde
 * tanklara SIĞACAK miktarı aşmaz; yolda olan sipariş öneriden düşülmüştür (ADR 0012 §14).
 */
function OrderSuggestionsPage(): ReactElement {
  const { t } = useTranslation();
  const permissions = useFuelPermissions();
  return (
    <FuelPage
      title={t("menu.fuelOrderSuggestions")}
      description={t("fuel.suggestions.description")}
      breadcrumb={[{ label: t("menu.fuelOrderSuggestions") }]}
    >
      {({ companyId }) =>
        permissions.canRead ? (
          <SuggestionsBody companyId={companyId} />
        ) : (
          <Alert tone="info">{t("fuel.noPermission")}</Alert>
        )
      }
    </FuelPage>
  );
}

function SuggestionsBody({ companyId }: { companyId: string }): ReactElement {
  const { t } = useTranslation();
  const station = useFuelStation(companyId);
  const suggestions = useOrderSuggestions(station.stationId);
  const permissions = useFuelPermissions();
  // Kalan süre metni ŞİMDİYE göre yazılır; `now` tek yerde üretilir ki kartlar aynı ana baksın.
  const now = useMemo(() => new Date(), []);
  const items = (suggestions.data?.items ?? []) as OrderSuggestion[];

  if (station.error) return <Alert tone="danger">{errorMessage(station.error)}</Alert>;
  if (station.isLoading) return <SkeletonRows rows={4} />;
  if (station.stationId === null) return <NoStations />;
  const stationId = station.stationId;

  return (
    <>
      <Card className="flex flex-wrap items-end justify-between gap-3 p-4">
        <StationField selection={station} />
        <Button variant="outline" asChild>
          <Link to="/akaryakit/siparisler" search={{ station: stationId }}>
            <ClipboardList aria-hidden="true" />
            {t("menu.fuelOrders")}
          </Link>
        </Button>
      </Card>

      {suggestions.error ? (
        <Alert tone="danger">{errorMessage(suggestions.error)}</Alert>
      ) : !suggestions.data ? (
        <SkeletonRows rows={6} />
      ) : items.length === 0 ? (
        <EmptyState
          headingLevel={2}
          icon={<PackagePlus />}
          title={t("fuel.suggestions.emptyTitle")}
          description={t("fuel.suggestions.emptyDescription")}
        />
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {t("fuel.suggestions.basis", {
              weeks: suggestions.data.forecastWeeks,
              at: formatDateTime(suggestions.data.generatedAt),
            })}
          </p>
          <div className="grid gap-4">
            {items.map((item) => (
              <SuggestionCard
                key={item.productId}
                item={item}
                stationId={stationId}
                now={now}
                canOrder={permissions.canManageOrders}
              />
            ))}
          </div>
        </>
      )}
    </>
  );
}

function SuggestionCard({
  item,
  stationId,
  now,
  canOrder,
}: {
  item: OrderSuggestion;
  stationId: string;
  now: Date;
  canOrder: boolean;
}): ReactElement {
  const { t } = useTranslation();
  const headingId = useId();
  const remaining = remainingTime(item.earliestRunOutAt, now);
  const soon = runsOutSoon(item.earliestRunOutAt, now);
  const prefill = buildOrderPrefill([{ productId: item.productId, quantityL: item.suggestedL }]);
  const suggested = Decimal.from(item.suggestedL);

  return (
    <Card className="grid gap-4 p-4 sm:p-5" aria-labelledby={headingId}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 id={headingId} className="text-base font-semibold">
          {item.productName}
        </h2>
        <StatusBadge tone={ORDER_URGENCY_TONE[item.urgency]}>
          {ORDER_URGENCY_LABELS[item.urgency]}
        </StatusBadge>
      </div>

      <dl className="grid gap-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <Field label={t("fuel.suggestions.fields.current")}>
          <QuantityText value={item.currentL} unit="LITRE" />
        </Field>
        <Field label={t("fuel.suggestions.fields.safeCapacity")}>
          <QuantityText value={item.safeCapacityL} unit="LITRE" />
        </Field>
        <Field label={t("fuel.suggestions.fields.dailyAverage")}>
          <QuantityText value={item.dailyAverageL} unit="LITRE" />
        </Field>
        <Field label={t("fuel.suggestions.fields.runOut")}>
          <span className="grid gap-0.5">
            <span className={soon ? "font-semibold text-warning" : undefined}>
              {formatDateTime(item.earliestRunOutAt)}
            </span>
            <span className="text-xs text-muted-foreground">
              {remaining === null
                ? t("fuel.suggestions.runOutUnknown")
                : remaining.past
                  ? t("fuel.suggestions.runOutPast")
                  : t("fuel.suggestions.runOutIn", { days: remaining.days, hours: remaining.hours })}
            </span>
          </span>
        </Field>
        <Field label={t("fuel.suggestions.fields.consumptionUntilDelivery")}>
          <span className="grid gap-0.5">
            <QuantityText value={item.consumptionUntilDeliveryL} unit="LITRE" />
            <span className="text-xs text-muted-foreground">
              {t("fuel.suggestions.leadTime", { hours: item.leadTimeHours })}
            </span>
          </span>
        </Field>
        <Field label={t("fuel.suggestions.fields.maxReceivable")}>
          <QuantityText value={item.maxReceivableL} unit="LITRE" />
        </Field>
        <Field label={t("fuel.suggestions.fields.minimumTarget")}>
          <QuantityText value={item.minimumTargetL} unit="LITRE" />
        </Field>
        <Field label={t("fuel.suggestions.fields.pendingOrder")}>
          <QuantityText value={item.pendingOrderL} unit="LITRE" />
        </Field>
      </dl>

      {soon ? <Alert tone="warning">{t("fuel.suggestions.runOutWarning")}</Alert> : null}
      {item.belowTarget ? <Alert tone="warning">{t("fuel.suggestions.belowTarget")}</Alert> : null}

      <div className="grid gap-2 rounded-lg border p-3">
        <p className="text-sm font-medium">
          {t("fuel.suggestions.suggested", { quantity: `${suggested.formatTr(3)} L` })}
        </p>
        {item.compartments.length === 0 ? (
          <p className="text-sm text-muted-foreground">{t("fuel.suggestions.noCompartments")}</p>
        ) : (
          <div
            className="overflow-x-auto"
            tabIndex={0}
            role="group"
            aria-label={t("fuel.suggestions.compartmentTable", { product: item.productName })}
          >
            <table className="w-full text-sm">
              <caption className="sr-only">
                {t("fuel.suggestions.compartmentTable", { product: item.productName })}
              </caption>
              <thead className="text-xs uppercase text-muted-foreground">
                <tr>
                  <Th>{t("fuel.suggestions.fields.compartmentNo")}</Th>
                  <Th align="right">{t("fuel.suggestions.fields.compartmentSize")}</Th>
                </tr>
              </thead>
              <tbody>
                {item.compartments.map((size, index) => (
                  <tr key={`${index}-${size}`} className="border-t">
                    <td className="px-3 py-2">{formatCount(index + 1)}</td>
                    <td className="px-3 py-2 text-right">
                      <QuantityText value={size} unit="LITRE" />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {canOrder && prefill !== "" ? (
          <div>
            <Button asChild>
              <Link to="/akaryakit/siparisler/yeni" search={{ station: stationId, prefill }}>
                <PackagePlus aria-hidden="true" />
                {t("fuel.suggestions.createOrder")}
              </Link>
            </Button>
          </div>
        ) : suggested.isZero() ? (
          <p className="text-sm text-muted-foreground">{t("fuel.suggestions.nothingToOrder")}</p>
        ) : null}
      </div>
    </Card>
  );
}
